import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Attachment } from '../types';
import { fileIcon, formatFileSize } from './attachmentUtils';

interface AttachmentListProps {
  attachments: Attachment[];
  bucket: string;
}

export default function AttachmentList({
  attachments,
  bucket,
}: AttachmentListProps) {
  const [urls, setUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!attachments.length) {
      setUrls({});
      return;
    }
    let cancelled = false;
    setUrls({});
    attachments.forEach(async (a) => {
      try {
        const { data } = await supabase.storage
          .from(bucket)
          .createSignedUrl(a.path, 3600);
        if (data?.signedUrl && !cancelled) {
          setUrls((prev) => ({ ...prev, [a.path]: data.signedUrl }));
        }
      } catch {
        // Bỏ qua file không tải được
      }
    });
    return () => {
      cancelled = true;
    };
  }, [attachments, bucket]);

  if (!attachments.length) return null;

  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-foreground-500">
        <i className="ri-attachment-2 text-sm" />
        File đính kèm ({attachments.length})
      </div>
      <div className="space-y-1.5">
        {attachments.map((a) => {
          const url = urls[a.path];
          return (
            <div
              key={a.path}
              className="flex items-center gap-2.5 rounded-lg border border-background-200 bg-background-50 px-3 py-2"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                <i className={`${fileIcon(a.name)} text-lg`} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground-900">
                  {a.name}
                </p>
                <p className="text-xs text-foreground-400">
                  {formatFileSize(a.size)}
                </p>
              </div>
              {url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-foreground-400 transition hover:bg-primary-100 hover:text-primary-700"
                  aria-label={`Tải ${a.name}`}
                  title="Tải xuống"
                >
                  <i className="ri-download-2-line text-lg" />
                </a>
              ) : (
                <span className="flex h-9 w-9 shrink-0 items-center justify-center">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-background-300 border-t-transparent" />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}